import {
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@monorepo/shared-auth/firebaseConfig';
import { sendLeagueInvitation } from './emailService';

// Generate a random invitation token
const generateToken = () => {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Create and send a league invitation
 * @param {Object} params - Invitation parameters
 * @param {string} params.leagueId - League ID
 * @param {string} params.leagueName - League name
 * @param {string} params.sport - Sport type
 * @param {string} params.inviterName - Name of person sending invitation
 * @param {string} params.inviterId - User ID of inviter
 * @param {string} params.inviteeEmail - Email of person being invited
 * @param {string} params.inviteeName - Name of person being invited (optional)
 * @param {string} params.role - Role for invitee (player, captain) (optional)
 * @param {string} params.teamName - Team name if inviting as captain (optional)
 */
export const createAndSendInvitation = async ({
  leagueId,
  leagueName,
  sport,
  inviterName,
  inviterId,
  inviteeEmail,
  inviteeName = '',
  role = 'player',
  teamName = null
}) => {
  try {
    // Generate secure token
    const token = generateToken();

    // Calculate expiry date (7 days from now)
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);

    // Store invitation in Firestore
    await setDoc(doc(db, 'leagueInvitations', token), {
      leagueId,
      leagueName,
      sport,
      inviterName,
      inviterId,
      inviteeEmail,
      inviteeName,
      role,
      teamName,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: expiryDate,
      usedAt: null
    });

    // Send email
    await sendLeagueInvitation({
      toEmail: inviteeEmail,
      toName: inviteeName || inviteeEmail.split('@')[0],
      leagueName,
      leagueId,
      inviterName,
      sport,
      invitationToken: token,
      role,
      teamName
    });

    return {
      success: true,
      token,
      message: `Invitation sent to ${inviteeEmail}`
    };
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
};

/**
 * Get invitation details by token
 * @param {string} token - Invitation token
 * @returns {Object|null} Invitation data or null if not found/expired
 */
export const getInvitation = async (token) => {
  try {
    const invitationDoc = await getDoc(doc(db, 'leagueInvitations', token));

    if (!invitationDoc.exists()) {
      return { error: 'Invitation not found' };
    }

    const invitation = invitationDoc.data();

    // Check if expired
    const now = new Date();
    const expiresAt = invitation.expiresAt?.toDate();

    if (expiresAt && expiresAt < now) {
      return { error: 'Invitation has expired' };
    }

    // Check if already used
    if (invitation.status === 'accepted') {
      return { error: 'Invitation has already been used' };
    }

    return {
      success: true,
      invitation: {
        leagueId: invitation.leagueId,
        leagueName: invitation.leagueName,
        sport: invitation.sport,
        inviterName: invitation.inviterName,
        inviteeEmail: invitation.inviteeEmail,
        inviteeName: invitation.inviteeName,
        role: invitation.role || 'player',
        teamName: invitation.teamName || null
      }
    };
  } catch (error) {
    console.error('Error getting invitation:', error);
    return { error: 'Failed to retrieve invitation' };
  }
};

/**
 * Accept an invitation (mark as used)
 * @param {string} token - Invitation token
 * @param {string} userId - User ID who accepted
 */
export const acceptInvitation = async (token, userId) => {
  try {
    const invitationRef = doc(db, 'leagueInvitations', token);
    const invitationDoc = await getDoc(invitationRef);

    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found');
    }

    // Update invitation status
    await setDoc(
      invitationRef,
      {
        status: 'accepted',
        usedAt: serverTimestamp(),
        acceptedBy: userId
      },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

/**
 * Delete an invitation
 * @param {string} token - Invitation token
 */
export const deleteInvitation = async (token) => {
  try {
    await deleteDoc(doc(db, 'leagueInvitations', token));
    return { success: true };
  } catch (error) {
    console.error('Error deleting invitation:', error);
    throw error;
  }
};

/**
 * Resend an invitation email
 * @param {string} token - Existing invitation token
 */
export const resendInvitation = async (token) => {
  try {
    const invitationDoc = await getDoc(doc(db, 'leagueInvitations', token));

    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = invitationDoc.data();

    // Send email again
    await sendLeagueInvitation({
      toEmail: invitation.inviteeEmail,
      toName: invitation.inviteeName || invitation.inviteeEmail.split('@')[0],
      leagueName: invitation.leagueName,
      leagueId: invitation.leagueId,
      inviterName: invitation.inviterName,
      sport: invitation.sport,
      invitationToken: token,
      role: invitation.role || 'player',
      teamName: invitation.teamName || null
    });

    return {
      success: true,
      message: `Invitation resent to ${invitation.inviteeEmail}`
    };
  } catch (error) {
    console.error('Error resending invitation:', error);
    throw error;
  }
};

export default {
  createAndSendInvitation,
  getInvitation,
  acceptInvitation,
  deleteInvitation,
  resendInvitation
};
